import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogContent, DialogActions, IconButton, Box,
    TextField, MenuItem, Button, Slide, Typography, Avatar, CircularProgress, Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useDispatch, useSelector } from 'react-redux';
import {
    searchEmployeeByEmail,
    clearEmployeeSearch,
    linkExistingEmployee,
} from '../../../../redux/actions/SocietyController/CtrlEmployeeAction/CtrlEmployeeAction';
import { employeeFullName } from './employeeConstants';
import { useSocietyRoles, roleToOption } from './useSocietyRoles';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const fieldSx = {
    '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#fff' },
    '& .MuiInputBase-input': { fontSize: 13 },
    '& .MuiInputLabel-root': { fontSize: 13 },
    '& .MuiFormHelperText-root': { fontSize: 11, marginLeft: 0 },
};

const Section = ({ icon, title, children }) => (
    <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Icon icon={icon} style={{ fontSize: 15, color: '#94a3b8' }} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#64748b', whiteSpace: 'nowrap' }}>
                {title}
            </Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: '#e5e9f0' }} />
        </Box>
        {children}
    </Box>
);

/**
 * Two-step "Add From Existing":
 *  1. Search a person by email GLOBALLY (no society) — they aren't in the target yet.
 *  2. Pick the TARGET community + a role/type → link-existing creates a new
 *     society-scoped row for the same login. The source row is untouched.
 */
const AddFromExistingModal = ({ open, societyOptions = [], typeOptions = [], initialEmail = '', onClose, onLinked }) => {
    const dispatch = useDispatch();
    const {
        searchedData, searchedLoading, searchedError, linkLoading,
    } = useSelector((state) => state.CtrlEmployeeReducer || {});

    const [email, setEmail] = useState('');
    const [targetSocietyId, setTargetSocietyId] = useState('');
    const [typeId, setTypeId] = useState('');
    const [roleId, setRoleId] = useState('');

    // RBAC roles for the target community's role picker.
    const { roles, loading: rolesLoading } = useSocietyRoles(open ? targetSocietyId : '');
    const roleOptions = roles.map(roleToOption).filter((o) => o.value != null && o.label);

    useEffect(() => {
        if (open) {
            setTargetSocietyId('');
            setTypeId('');
            setRoleId('');
            dispatch(clearEmployeeSearch());
            const seed = (initialEmail || '').trim();
            setEmail(seed);
            // Duplicate-guard routing: auto-search the email carried over from Create.
            if (seed && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(seed)) {
                dispatch(searchEmployeeByEmail({ email: seed }));
            }
        }
    }, [open, initialEmail, dispatch]);

    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

    const handleSearch = (e) => {
        e?.preventDefault?.();
        if (!emailValid) return;
        // Reset the step-2 choices for a fresh match, then search globally by email.
        setTargetSocietyId('');
        setTypeId('');
        setRoleId('');
        dispatch(searchEmployeeByEmail({ email: email.trim() }));
    };

    const handleLink = () => {
        if (!searchedData || !targetSocietyId || !typeId) return;
        dispatch(linkExistingEmployee({
            employee_id: searchedData.employee_id,
            society_id: targetSocietyId,
            employee_type_id: typeId,
            role_id: roleId || undefined,
            onSuccess: () => { onLinked?.(); },
        }));
    };

    const currentSociety = searchedData?.society?.name;
    const currentType = searchedData?.employeeType?.type;

    return (
        <Dialog open={open} onClose={linkLoading ? undefined : onClose} TransitionComponent={Transition}
            maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
            {/* Header */}
            <Box sx={{ px: 3, py: 2.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #eef1f6' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: 2.5, bgcolor: '#eef4ff', color: '#487fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Icon icon="solar:user-plus-rounded-bold-duotone" style={{ fontSize: 24 }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>Add From Existing</Typography>
                        <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>Add a person who already works elsewhere to a community</Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} disabled={linkLoading} size="small"
                    sx={{ color: '#64748b', bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <DialogContent sx={{ px: 3, py: 2.5, bgcolor: '#fff' }}>
                {/* Step 1 — global email search */}
                <Section icon="solar:magnifer-linear" title="Step 1 · Find Person">
                    <form onSubmit={handleSearch}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                            <TextField label="Email" type="email" size="small" fullWidth sx={fieldSx}
                                placeholder="person@example.com"
                                value={email} onChange={(e) => setEmail(e.target.value)}
                                helperText="Searched across all communities" />
                            <Button type="submit" variant="contained" disableElevation
                                disabled={!emailValid || searchedLoading}
                                startIcon={searchedLoading
                                    ? <CircularProgress size={14} sx={{ color: '#fff' }} />
                                    : <Icon icon="solar:magnifer-linear" style={{ fontSize: 16 }} />}
                                sx={{ backgroundColor: '#487fff', textTransform: 'none', fontWeight: 600, borderRadius: 2, whiteSpace: 'nowrap', height: 40 }}>
                                Search
                            </Button>
                        </Box>
                    </form>

                    {searchedError && !searchedLoading && (
                        <Box sx={{ mt: 1.5, p: 1.25, borderRadius: 2, bgcolor: '#fef2f2', border: '1px solid #fee2e2', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Icon icon="solar:danger-triangle-linear" style={{ fontSize: 16, color: '#dc2626', flexShrink: 0 }} />
                            <Typography sx={{ fontSize: 12.5, color: '#b91c1c' }}>{searchedError}</Typography>
                        </Box>
                    )}

                    {searchedData && (
                        <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e5e9f0', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ bgcolor: '#487fff', width: 40, height: 40, fontWeight: 700 }}>
                                {employeeFullName(searchedData).charAt(0).toUpperCase()}
                            </Avatar>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#1e293b' }}>{employeeFullName(searchedData)}</Typography>
                                <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                                    {searchedData.email}{searchedData.phone ? ` · ${searchedData.phone}` : ''}
                                </Typography>
                                {(currentSociety || currentType) && (
                                    <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                        {currentSociety && <Chip size="small" label={currentSociety} sx={{ height: 20, fontSize: 10.5, bgcolor: '#eef4ff', color: '#3f6ede' }} />}
                                        {currentType && <Chip size="small" label={currentType} sx={{ height: 20, fontSize: 10.5, bgcolor: '#f1f5f9', color: '#475569' }} />}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </Section>

                {/* Step 2 — target community + role (only after a match) */}
                {searchedData && (
                    <Section icon="solar:map-arrow-square-linear" title="Step 2 · Add To Community">
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                            <TextField select label="Target Community" size="small" fullWidth required sx={fieldSx}
                                value={targetSocietyId} onChange={(e) => { setTargetSocietyId(e.target.value); setRoleId(''); }}>
                                <MenuItem value=""><em>Select community</em></MenuItem>
                                {societyOptions.map((o) => (<MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>))}
                            </TextField>
                            <TextField select label="Employee Type" size="small" fullWidth required sx={fieldSx}
                                value={typeId} onChange={(e) => setTypeId(e.target.value)}>
                                <MenuItem value=""><em>Select type</em></MenuItem>
                                {typeOptions.map((o) => (<MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>))}
                            </TextField>
                            <TextField select label="Access Role" size="small" fullWidth sx={fieldSx}
                                value={roleId} onChange={(e) => setRoleId(e.target.value)}
                                disabled={!targetSocietyId || rolesLoading}
                                helperText={rolesLoading ? 'Loading roles…' : 'Optional — defaults from type'}>
                                <MenuItem value=""><em>Default (from type)</em></MenuItem>
                                {roleOptions.map((o) => (<MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>))}
                            </TextField>
                        </Box>
                        <Box sx={{ mt: 1.25, display: 'flex', alignItems: 'center', gap: 0.75, color: '#94a3b8' }}>
                            <Icon icon="solar:info-circle-linear" style={{ fontSize: 14, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: 11.5 }}>
                                Creates a new record in the selected community, linked to the same login. Their existing record stays unchanged.
                            </Typography>
                        </Box>
                    </Section>
                )}

                {!searchedData && !searchedError && !searchedLoading && (
                    <Box sx={{ textAlign: 'center', py: 1, color: '#94a3b8' }}>
                        <Typography sx={{ fontSize: 12 }}>Search an email above to continue.</Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, gap: 1.25, borderTop: '1px solid #eef1f6', bgcolor: '#fbfcfe' }}>
                <Button onClick={onClose} disabled={linkLoading} variant="text"
                    sx={{ color: '#475569', fontWeight: 600, textTransform: 'none', px: 2, '&:hover': { bgcolor: '#f1f5f9' } }}>
                    Cancel
                </Button>
                <Button onClick={handleLink} disabled={!searchedData || !targetSocietyId || !typeId || linkLoading}
                    variant="contained" disableElevation
                    startIcon={linkLoading
                        ? <CircularProgress size={15} sx={{ color: '#fff' }} />
                        : <Icon icon="solar:add-circle-bold" style={{ fontSize: 17 }} />}
                    sx={{ backgroundColor: '#487fff', textTransform: 'none', fontWeight: 600, px: 2.5, borderRadius: 2, '&:hover': { backgroundColor: '#3f6ede' } }}>
                    {linkLoading ? 'Adding…' : 'Add to Community'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddFromExistingModal;
